import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { setMailPassword } from '../utils/mailCredentials';
import './RegisterWizard.css';

const EMAIL_DOMAIN = '@atozasmail.in';
const PASSWORD_STRONG_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const MONTHS = [
  { value: '', label: 'Month' },
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const GENDERS = [
  { value: '', label: 'Select gender' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

const STEPS = [
  {
    id: 'name',
    title: 'Create your',
    titleAccent: 'account',
    subtitle: 'Enter your name to begin setting up ATOZASMail.',
  },
  {
    id: 'birthday',
    title: 'About',
    titleAccent: 'you',
    subtitle: 'We use this to personalize your workspace (you must be 13+).',
  },
  {
    id: 'email',
    title: 'Choose your',
    titleAccent: 'email',
    subtitle: 'Pick a suggested address or create your own @atozasmail.in ID.',
  },
  {
    id: 'recovery',
    title: 'Add a',
    titleAccent: 'recovery email',
    subtitle: 'Use Gmail, Yahoo, or another inbox so you can reset your password later.',
  },
  {
    id: 'password',
    title: 'Secure your',
    titleAccent: 'inbox',
    subtitle: 'Use letters, numbers, and symbols for a strong password.',
  },
];

const RECOVERY_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const sanitizeLocalPart = (value) => value.toLowerCase().replace(/[^a-z0-9._-]/g, '');

const buildEmailSuggestions = (firstName, lastName) => {
  const f = sanitizeLocalPart(firstName);
  const l = sanitizeLocalPart(lastName);
  const suggestions = [];

  if (f && l) {
    suggestions.push(`${f}${l}`);
    suggestions.push(`${f}.${l}`);
  } else if (f) {
    suggestions.push(f);
    suggestions.push(`${f}${Math.floor(Math.random() * 900) + 100}`);
  }

  return [...new Set(suggestions.filter((s) => s.length >= 3))].slice(0, 2);
};

const isValidBirthDate = (month, day, year) => {
  const m = Number(month);
  const d = Number(day);
  const y = Number(year);
  if (!m || !d || !y) return false;
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
};

const isAtLeastAge = (month, day, year, minAge = 13) => {
  const birth = new Date(Number(year), Number(month) - 1, Number(day));
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age >= minAge;
};

const FormField = ({
  id,
  label,
  value,
  onChange,
  type = 'text',
  placeholder = '',
  autoFocus = false,
  disabled = false,
}) => (
  <div className="rw-field">
    <label htmlFor={id}>{label}</label>
    <input
      id={id}
      className="rw-input"
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoFocus={autoFocus}
      disabled={disabled}
      autoComplete={type === 'password' ? 'new-password' : 'off'}
    />
  </div>
);

const FormSelect = ({ id, label, value, onChange, options, disabled = false }) => (
  <div className="rw-field">
    <label htmlFor={id}>{label}</label>
    <select
      id={id}
      className="rw-select"
      value={value}
      onChange={onChange}
      disabled={disabled}
    >
      {options.map((opt) => (
        <option
          key={opt.value || 'empty'}
          value={opt.value}
          disabled={opt.value === ''}
          hidden={opt.value === ''}
        >
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

const EmailCustomField = ({ value, onChange, disabled }) => (
  <div className="rw-email-custom">
    <div className="rw-field">
      <label htmlFor="emailLocal">Custom address</label>
      <div className="rw-email-input-wrap">
        <input
          id="emailLocal"
          className="rw-input"
          type="text"
          value={value}
          onChange={(e) => onChange(sanitizeLocalPart(e.target.value))}
          placeholder="username"
          disabled={disabled}
          autoComplete="off"
        />
        <span className="rw-email-domain">{EMAIL_DOMAIN}</span>
      </div>
      <p className="rw-hint">Letters, numbers, and periods only</p>
    </div>
  </div>
);

const RegisterWizard = ({ onLogin, isAddingAccount = false, onSwitchToLogin }) => {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    birthMonth: '',
    birthDay: '',
    birthYear: '',
    gender: '',
    emailChoice: 'custom',
    emailLocal: '',
    recoveryEmail: '',
    password: '',
    confirmPassword: '',
  });

  const step = STEPS[stepIndex];
  const progressPercent = ((stepIndex + 1) / STEPS.length) * 100;
  const emailSuggestions = useMemo(
    () => buildEmailSuggestions(form.firstName, form.lastName),
    [form.firstName, form.lastName]
  );

  const getOrCreateDeviceId = () => {
    const key = 'deviceId';
    let deviceId = localStorage.getItem(key);
    if (!deviceId) {
      const randomPart = Math.random().toString(36).slice(2, 12);
      deviceId = `dev_${Date.now()}_${randomPart}`;
      localStorage.setItem(key, deviceId);
    }
    return deviceId;
  };

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (message.text) setMessage({ type: '', text: '' });
  };

  const getSelectedEmailLocal = () => {
    if (form.emailChoice === 'custom') {
      return sanitizeLocalPart(form.emailLocal);
    }
    if (form.emailChoice === 'suggestion0') return emailSuggestions[0] || '';
    if (form.emailChoice === 'suggestion1') return emailSuggestions[1] || '';
    return sanitizeLocalPart(form.emailLocal);
  };

  const getFullEmail = () => `${getSelectedEmailLocal()}${EMAIL_DOMAIN}`;

  const validateStep = () => {
    if (step.id === 'name') {
      if (!form.firstName.trim()) {
        return 'Enter your first name';
      }
      return '';
    }

    if (step.id === 'birthday') {
      if (!form.birthMonth || !form.birthDay || !form.birthYear) {
        return 'Enter your full birthday';
      }
      if (!isValidBirthDate(form.birthMonth, form.birthDay, form.birthYear)) {
        return 'Enter a valid birthday';
      }
      if (!isAtLeastAge(form.birthMonth, form.birthDay, form.birthYear)) {
        return 'You must be at least 13 years old to create an account';
      }
      if (!form.gender || !['Male', 'Female'].includes(form.gender)) {
        return 'Please select Male or Female';
      }
      return '';
    }

    if (step.id === 'email') {
      const local = getSelectedEmailLocal();
      if (!local || local.length < 3) {
        return 'Choose or create a valid email address (at least 3 characters)';
      }
      if (!/^[a-z0-9._-]+$/.test(local)) {
        return 'You can use letters, numbers & periods';
      }
      return '';
    }

    if (step.id === 'recovery') {
      const recovery = form.recoveryEmail.trim().toLowerCase();
      if (!recovery || !RECOVERY_EMAIL_REGEX.test(recovery)) {
        return 'Enter a valid recovery email address';
      }
      if (/@atozasmail\.in$/i.test(recovery)) {
        return 'Use an external email like Gmail or Yahoo — not @atozasmail.in';
      }
      if (recovery === getFullEmail().toLowerCase()) {
        return 'Recovery email must be different from your ATOZASMail address';
      }
      return '';
    }

    if (step.id === 'password') {
      if (!PASSWORD_STRONG_REGEX.test(form.password)) {
        return 'Use at least 8 characters with letters, numbers, and symbols';
      }
      if (form.password !== form.confirmPassword) {
        return 'Passwords do not match';
      }
      return '';
    }

    return '';
  };

  const handleNext = async () => {
    const error = validateStep();
    if (error) {
      setMessage({ type: 'error', text: error });
      return;
    }

    if (stepIndex < STEPS.length - 1) {
      if (step.id === 'birthday') {
        setForm((prev) => ({
          ...prev,
          emailChoice: emailSuggestions.length > 0 ? 'suggestion0' : 'custom',
          emailLocal: emailSuggestions[0] || prev.emailLocal,
        }));
      }
      setStepIndex((i) => i + 1);
      setMessage({ type: '', text: '' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const email = getFullEmail();
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        birthMonth: Number(form.birthMonth),
        birthDay: Number(form.birthDay),
        birthYear: Number(form.birthYear),
        gender: form.gender,
        email,
        username: getSelectedEmailLocal(),
        recoveryEmail: form.recoveryEmail.trim().toLowerCase(),
        password: form.password,
        deviceId: getOrCreateDeviceId(),
      };

      const response = await axiosInstance.post('/api/auth/register', payload);

      if (response.data.success) {
        const { user, token } = response.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setMailPassword(form.password, email);
        onLogin(user, isAddingAccount);
        navigate('/inbox', { replace: true });
      } else {
        setMessage({
          type: 'error',
          text: response.data.message || 'Registration failed',
        });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Registration failed. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
      setMessage({ type: '', text: '' });
    }
  };

  const renderFields = () => {
    if (step.id === 'name') {
      return (
        <div className="register-fields">
          <FormField
            id="firstName"
            label="First name"
            value={form.firstName}
            onChange={(e) => updateField('firstName', e.target.value)}
            placeholder="First name"
            autoFocus
            disabled={loading}
          />
          <FormField
            id="lastName"
            label="Last name (optional)"
            value={form.lastName}
            onChange={(e) => updateField('lastName', e.target.value)}
            placeholder="Last name"
            disabled={loading}
          />
        </div>
      );
    }

    if (step.id === 'birthday') {
      return (
        <div className="register-fields">
          <div className="register-birthday-row">
            <FormSelect
              id="birthMonth"
              label="Month"
              value={form.birthMonth}
              onChange={(e) => updateField('birthMonth', e.target.value)}
              options={MONTHS}
              disabled={loading}
            />
            <FormField
              id="birthDay"
              label="Day"
              value={form.birthDay}
              onChange={(e) => updateField('birthDay', e.target.value.replace(/\D/g, '').slice(0, 2))}
              placeholder="DD"
              disabled={loading}
            />
            <FormField
              id="birthYear"
              label="Year"
              value={form.birthYear}
              onChange={(e) => updateField('birthYear', e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="YYYY"
              disabled={loading}
            />
          </div>
          <FormSelect
            id="gender"
            label="Gender"
            value={form.gender}
            onChange={(e) => updateField('gender', e.target.value)}
            options={GENDERS}
            disabled={loading}
          />
        </div>
      );
    }

    if (step.id === 'email') {
      const customSelected = form.emailChoice === 'custom';
      return (
        <div className="register-fields rw-email-options">
          {emailSuggestions.map((suggestion, index) => (
            <label key={suggestion} className="rw-radio-card">
              <input
                type="radio"
                name="emailChoice"
                checked={form.emailChoice === `suggestion${index}`}
                onChange={() => updateField('emailChoice', `suggestion${index}`)}
                disabled={loading}
              />
              <span className="rw-radio-label">
                {suggestion}
                {EMAIL_DOMAIN}
              </span>
            </label>
          ))}
          <label className="rw-radio-card">
            <input
              type="radio"
              name="emailChoice"
              checked={customSelected}
              onChange={() => updateField('emailChoice', 'custom')}
              disabled={loading}
            />
            <span className="rw-radio-label">Create your own address</span>
          </label>
          {customSelected && (
            <EmailCustomField
              value={form.emailLocal}
              onChange={(v) => updateField('emailLocal', v)}
              disabled={loading}
            />
          )}
        </div>
      );
    }

    if (step.id === 'recovery') {
      return (
        <div className="register-fields">
          <FormField
            id="recoveryEmail"
            label="Recovery email"
            type="email"
            value={form.recoveryEmail}
            onChange={(e) => updateField('recoveryEmail', e.target.value)}
            placeholder="you@gmail.com"
            autoFocus
            disabled={loading}
          />
          <p className="register-field-hint">
            We&apos;ll send a password reset link here if you forget your password.
          </p>
        </div>
      );
    }

    return (
      <div className="register-fields">
        <FormField
          id="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={form.password}
          onChange={(e) => updateField('password', e.target.value)}
          placeholder="Enter password"
          autoFocus
          disabled={loading}
        />
        <FormField
          id="confirmPassword"
          label="Confirm password"
          type={showPassword ? 'text' : 'password'}
          value={form.confirmPassword}
          onChange={(e) => updateField('confirmPassword', e.target.value)}
          placeholder="Re-enter password"
          disabled={loading}
        />
        <label className="rw-checkbox-row">
          <input
            type="checkbox"
            checked={showPassword}
            onChange={(e) => setShowPassword(e.target.checked)}
            disabled={loading}
          />
          Show password
        </label>
      </div>
    );
  };

  return (
    <div className="register-page">
      <div className="register-shell">
        <div className="register-top-bar">
          <button
            type="button"
            className="register-top-link"
            onClick={() => navigate('/')}
          >
            ← Back to home
          </button>
        </div>

        <div className="register-card">
          <div className="register-progress" aria-hidden="true">
            <p className="register-progress-meta">
              Step {stepIndex + 1} of {STEPS.length}
            </p>
            <div className="register-progress-bar">
              <div
                className="register-progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <div className="register-intro">
            <img className="register-logo" src="/atozasmail-logo.png" alt="ATOZASMail" />
            <h1 className="register-title">
              {step.title} <span>{step.titleAccent}</span>
            </h1>
            <p className="register-subtitle">{step.subtitle}</p>
          </div>

          <div className="register-form-panel">
            <div className="register-fields-wrap">
              {message.text && (
                <div className={`register-message ${message.type}`}>{message.text}</div>
              )}
              {renderFields()}
            </div>
            <div className="register-actions">
              {stepIndex > 0 && (
                <button
                  type="button"
                  className="register-back-btn"
                  onClick={handleBack}
                  disabled={loading}
                >
                  Back
                </button>
              )}
              <button
                type="button"
                className="register-next-btn"
                onClick={handleNext}
                disabled={loading}
              >
                {loading
                  ? 'Please wait...'
                  : stepIndex === STEPS.length - 1
                  ? 'Create account'
                  : 'Next'}
              </button>
            </div>
          </div>
        </div>

        <p className="register-footer-link">
          Already have an account?{' '}
          <button type="button" onClick={onSwitchToLogin} disabled={loading}>
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default RegisterWizard;
